import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { safeParseJson } from '../lib/storage-utils';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AnxietyVsReality } from '../components/AnxietyVsReality';
import { AttachmentJourney } from '../components/AttachmentJourney';
import { RuminationDetection } from '../components/RuminationDetection';
import { TherapistExport } from '../components/TherapistExport';
import { TriggerInsights } from '../components/TriggerInsights';
import { EnhancedShareButton } from '../components/EnhancedShareButton';
import { InfoHint } from '../components/InfoHint';
import { ArrowLeft, TrendingUp, TrendingDown, Clock, Calendar, AlertTriangle, Crown, Lock, Heart, Zap, Target, Brain } from 'lucide-react';
import { getLatestEIAssessmentSnapshot } from '../lib/ei-assessment';

export default function Insights() {
  const navigate = useNavigate();
  const subscription = localStorage.getItem('tension_subscription') || 'free';
  const isPremium = subscription === 'premium' || subscription === 'pro';
  const isPro = subscription === 'pro';

  const [insights, setInsights] = useState<any>(null);
  const [eiSnapshot, setEiSnapshot] = useState<any>(null);

  const activeRelationshipId = localStorage.getItem('tension_active_relationship');
  const relationshipsRaw = safeParseJson<unknown>(localStorage.getItem('tension_relationships'), []);
  const relationships = Array.isArray(relationshipsRaw) ? relationshipsRaw : [];
  const activeRelationship = relationships.find((r: any) => r.id === activeRelationshipId);

  useEffect(() => {
    setEiSnapshot(getLatestEIAssessmentSnapshot());
  }, []);

  useEffect(() => {
    if (isPremium) {
      // Generate insights from check-in data for active relationship
      const allCheckins = safeParseJson<unknown[]>(localStorage.getItem('tension_checkins'), []);
      const checkinsList = Array.isArray(allCheckins) ? allCheckins : [];
      
      const checkins = activeRelationshipId
        ? checkinsList.filter((c: any) => c.relationshipId === activeRelationshipId)
        : checkinsList;

      if (checkins.length >= 3) {
        const scores = checkins.map((c: any) => c.rtiScore || 0);
        const avgScore = Math.round(scores.reduce((a: number, b: number) => a + b, 0) / scores.length);

        // Detect patterns
        const patterns = [];
        
        // Emotional state analysis
        const emotionalStates = checkins
          .filter((c: any) => c.emotionalStates && c.emotionalStates.length > 0)
          .flatMap((c: any) => c.emotionalStates);
        
        if (emotionalStates.length > 0) {
          const anxiousCount = emotionalStates.filter((s: number) => s === 1).length;
          const connectedCount = emotionalStates.filter((s: number) => s === 5).length;
          
          if (anxiousCount > emotionalStates.length * 0.4) {
            patterns.push({
              title: 'Frequent Anxiety Days',
              description: 'Many check-ins show anxiety. Some tension may be coming from stress or fear, not only from your partner.',
              type: 'warning',
              category: 'emotional',
            });
          }
          
          if (connectedCount > emotionalStates.length * 0.5) {
            patterns.push({
              title: 'Strong Connection Base',
              description: 'You often feel connected. That is a real strength and a good base to protect.',
              type: 'positive',
              category: 'emotional',
            });
          }
        }
        
        // Time of day pattern
        const morningCheckins = checkins.filter((c: any) => {
          const hour = new Date(c.timestamp).getHours();
          return hour >= 6 && hour < 12;
        });
        const eveningCheckins = checkins.filter((c: any) => {
          const hour = new Date(c.timestamp).getHours();
          return hour >= 18 && hour < 24;
        });

        if (eveningCheckins.length > morningCheckins.length * 2) {
          patterns.push({
            title: 'Mostly Evening Check-Ins',
            description: 'You check in mostly at night. Scores may include end-of-day stress, not just relationship issues.',
            type: 'timing',
            category: 'behavioral',
          });
        }

        // Tension trajectory
        if (scores.length >= 5) {
          const recent5 = scores.slice(-5);
          const increasing = recent5.every((score, i) => i === 0 || score >= recent5[i - 1]);
          const decreasing = recent5.every((score, i) => i === 0 || score <= recent5[i - 1]);
          
          if (increasing) {
            patterns.push({
              title: 'Tension Is Climbing',
              description: 'Your score rose across 5 check-ins in a row. Slowing down and reducing pressure can help quickly.',
              type: 'warning',
              category: 'trajectory',
            });
          } else if (decreasing) {
            patterns.push({
              title: 'Tension Is Improving',
              description: 'Your score dropped across 5 check-ins in a row. What you are doing now is working.',
              type: 'positive',
              category: 'trajectory',
            });
          }
        }

        // Component analysis - initiation imbalance
        const initiationScores = checkins
          .filter((c: any) => c.components?.initiation)
          .map((c: any) => c.components.initiation);
        
        if (initiationScores.length >= 3) {
          const avgInitiation = initiationScores.reduce((a: number, b: number) => a + b, 0) / initiationScores.length;
          
          if (avgInitiation > 70) {
            patterns.push({
              title: 'You Start Most Contact',
              description: 'You are initiating most interactions. Pulling back slightly can restore balance and lower tension.',
              type: 'insight',
              category: 'behavioral',
            });
          }
        }

        // Withdrawal detection
        const withdrawalScores = checkins
          .filter((c: any) => c.components?.withdrawal)
          .map((c: any) => c.components.withdrawal);
        
        if (withdrawalScores.length >= 3) {
          const avgWithdrawal = withdrawalScores.reduce((a: number, b: number) => a + b, 0) / withdrawalScores.length;
          
          if (avgWithdrawal > 60) {
            patterns.push({
              title: 'Distance Signals Showing Up',
              description: 'Withdrawal signs are high. A calm reconnection conversation may help both of you feel closer again.',
              type: 'warning',
              category: 'behavioral',
            });
          }
        }

        setInsights({
          patterns,
          avgScore,
          checkInCount: checkins.length,
          emotionalDistribution: {
            anxious: emotionalStates.filter((s: number) => s === 1).length,
            uncertain: emotionalStates.filter((s: number) => s === 2).length,
            neutral: emotionalStates.filter((s: number) => s === 3).length,
            hopeful: emotionalStates.filter((s: number) => s === 4).length,
            connected: emotionalStates.filter((s: number) => s === 5).length,
          },
          componentAverages: {
            initiation: initiationScores.length > 0 
              ? Math.round(initiationScores.reduce((a: number, b: number) => a + b, 0) / initiationScores.length) 
              : null,
            withdrawal: withdrawalScores.length > 0 
              ? Math.round(withdrawalScores.reduce((a: number, b: number) => a + b, 0) / withdrawalScores.length) 
              : null,
          }
        });
      }
    }
  }, [isPremium, activeRelationshipId]);

  if (!isPremium) {
    return (
      <div className="min-h-screen tension-page-bg">
        {/* Header */}
        <div className="border-b border-neutral-200 bg-white px-6 py-4 dark:border-neutral-800 dark:bg-neutral-950/90">
          <div className="mx-auto flex max-w-2xl items-center gap-4">
            <button
              onClick={() => navigate('/home')}
              className="rounded-lg p-2 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
            >
              <ArrowLeft className="h-5 w-5 text-neutral-600 dark:text-neutral-300" />
            </button>
            <h1 className="text-xl font-light text-neutral-900 dark:text-neutral-50 inline-flex items-center gap-1.5">
              Pattern Insights
              <InfoHint
                title="Pattern Insights"
                description="This page turns your check-ins into easy trends, so you can see what helps and what raises tension."
              />
            </h1>
          </div>
        </div>

        <div className="mx-auto max-w-2xl px-6 py-12">
          <Card className="space-y-6 p-8 text-center">
            <Lock className="mx-auto h-16 w-16 text-neutral-400 dark:text-neutral-500" />
            <div className="space-y-2">
              <h2 className="text-2xl font-light text-neutral-900 dark:text-neutral-50">Premium Feature</h2>
              <p className="text-neutral-600 dark:text-neutral-300">
                Get AI-powered pattern insights that reveal behavioral trends and predict tension escalation.
              </p>
            </div>
            <div className="space-y-3 rounded-lg bg-neutral-50 p-6 text-left dark:bg-neutral-900/60">
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Unlock with Premium:</p>
              <ul className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
                <li>• Behavioral pattern detection</li>
                <li>• Time-of-day tension analysis</li>
                <li>• Initiation-response dynamics</li>
                <li>• Emotional volatility tracking</li>
                <li>• Predictive risk alerts</li>
              </ul>
            </div>
            <Button onClick={() => navigate('/pricing')} size="lg">
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Premium
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="border-b border-neutral-200 bg-white px-6 py-4 dark:border-neutral-800 dark:bg-neutral-950/90">
        <div className="mx-auto flex max-w-2xl items-center gap-4">
          <button
            onClick={() => navigate('/home')}
            className="rounded-lg p-2 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <ArrowLeft className="h-5 w-5 text-neutral-600 dark:text-neutral-300" />
          </button>
          <h1 className="text-xl font-light text-neutral-900 dark:text-neutral-50 inline-flex items-center gap-1.5">
            Pattern Insights
            <InfoHint
              title="Pattern Insights"
              description="This page turns your check-ins into easy trends, so you can see what helps and what raises tension."
            />
          </h1>
          <div className="ml-auto flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
            <Crown className="w-4 h-4" />
            <span>Premium</span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-2xl space-y-6 px-6 py-12">
        {eiSnapshot && (
          <Card className="border-violet-200 bg-violet-50/40 p-6 dark:border-violet-500/30 dark:bg-violet-950/40">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-violet-800 dark:text-violet-200 inline-flex items-center gap-1.5">
                  EI Assessment Journey
                  <InfoHint
                    title="EI Assessment Journey"
                    description="Your emotional intelligence progress over time. Higher scores usually mean better self-regulation and communication."
                  />
                </p>
                <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-300">
                  Latest overall score: <strong>{eiSnapshot.overallScore}</strong>/100
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/home#relationship-intelligence')}>
                Retake
              </Button>
            </div>
          </Card>
        )}
        {!insights ? (
          <Card className="p-8 text-center">
            <p className="text-neutral-600 dark:text-neutral-300">
              Not enough data yet. Complete at least 3 check-ins to see pattern insights.
            </p>
          </Card>
        ) : (
          <>
            {/* Overview */}
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50 p-6 dark:border-blue-500/25 dark:from-blue-950/50 dark:to-violet-950/40">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 inline-flex items-center gap-1.5">
                    Your Pattern Summary
                    <InfoHint
                      title="Pattern Summary"
                      description="A quick snapshot of how much data we have and your average tension score."
                    />
                  </h2>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg bg-white/60 p-4 dark:bg-neutral-900/50">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">Check-Ins</p>
                    <p className="text-2xl font-light text-neutral-900 dark:text-neutral-50">{insights.checkInCount}</p>
                  </div>
                  <div className="rounded-lg bg-white/60 p-4 dark:bg-neutral-900/50">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 inline-flex items-center gap-1.5">
                      Avg score
                      <InfoHint
                        title="Average score"
                        description="Your average tension score. Lower is calmer, higher means more pressure in the relationship dynamic."
                      />
                    </p>
                    <p className="text-2xl font-light text-neutral-900 dark:text-neutral-50">{insights.avgScore}</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Detected Patterns */}
            {insights.patterns.length > 0 ? (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 inline-flex items-center gap-1.5">
                  Detected Patterns
                  <InfoHint
                    title="Detected Patterns"
                    description="Repeated trends found in your check-ins. These are signals, not judgments."
                  />
                </h3>
                {insights.patterns.map((pattern: any, index: number) => (
                  <Card
                    key={index}
                    className={`p-6 ${
                      pattern.type === 'warning'
                        ? 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-950/35'
                        : pattern.type === 'positive'
                          ? 'border-green-200 bg-green-50 dark:border-green-500/30 dark:bg-green-950/35'
                          : 'border-blue-200 bg-blue-50 dark:border-blue-500/30 dark:bg-blue-950/35'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {pattern.type === 'warning' && (
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                      )}
                      {pattern.type === 'positive' && (
                        <TrendingDown className="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
                      )}
                      {pattern.type === 'timing' && (
                        <Clock className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
                      )}
                      {pattern.type === 'insight' && (
                        <TrendingUp className="mt-0.5 h-5 w-5 shrink-0 text-purple-600 dark:text-purple-400" />
                      )}
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium text-neutral-900 dark:text-neutral-50">{pattern.title}</h4>
                        <p className="mt-1 text-sm text-neutral-700 dark:text-neutral-200">{pattern.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-6 text-center">
                <p className="text-neutral-600 dark:text-neutral-300">
                  Keep checking in daily. Patterns will emerge as we gather more data points.
                </p>
              </Card>
            )}

            {/* Trigger Insights - Available for all Premium/Pro users */}
            <TriggerInsights />

            {/* Behavioral Insights */}
            <Card className="p-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 inline-flex items-center gap-1.5">
                  Behavioral Insights
                  <InfoHint
                    title="Behavioral Insights"
                    description="Actionable observations from your habits and score shifts. Keep what works, adjust what adds pressure."
                  />
                </h3>
                <div className="space-y-3 text-sm text-neutral-700 dark:text-neutral-200">
                  <div className="flex items-start gap-2">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                    <p>
                      <strong className="text-neutral-900 dark:text-neutral-100">Restraint Effectiveness:</strong>{' '}
                      Based on your data, strategic restraint typically reduces tension by 15-25 points within 48 hours.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-purple-500" />
                    <p>
                      <strong className="text-neutral-900 dark:text-neutral-100">Response Patterns:</strong> Your
                      emotional density scores suggest high investment. Balance intensity with moments of lightness.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-green-500" />
                    <p>
                      <strong className="text-neutral-900 dark:text-neutral-100">Best Check-In Time:</strong> Evening
                      check-ins provide the most accurate daily snapshots after full interaction cycles.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Recommendations */}
            <Card className="p-6 bg-neutral-900 text-white">
              <div className="space-y-4">
                <h3 className="text-lg font-medium inline-flex items-center gap-1.5">
                  Personalized Recommendations
                  <InfoHint
                    title="Personalized Recommendations"
                    description="Simple next steps based on your average score and recent trend."
                  />
                </h3>
                <ul className="space-y-2 text-sm text-neutral-300">
                  {insights.avgScore > 60 && (
                    <>
                      <li>• Implement 72-hour restraint cycle: Reduce initiation by 40%</li>
                      <li>• Focus on quality over quantity in interactions</li>
                      <li>• Allow them to miss you before re-engaging</li>
                    </>
                  )}
                  {insights.avgScore > 35 && insights.avgScore <= 60 && (
                    <>
                      <li>• Maintain current balance with minor adjustments</li>
                      <li>• Plan one meaningful conversation per week</li>
                      <li>• Monitor for escalation patterns</li>
                    </>
                  )}
                  {insights.avgScore <= 35 && (
                    <>
                      <li>• Continue current communication rhythm</li>
                      <li>• Your patterns show healthy equilibrium</li>
                      <li>• Stay consistent to maintain stability</li>
                    </>
                  )}
                </ul>
              </div>
            </Card>

            {/* Phase 4C: Pro-Exclusive Components */}
            {isPro && activeRelationshipId && (
              <>
                {/* Rumination Detection */}
                <RuminationDetection tier={subscription as any} />

                {/* Anxiety vs. Reality Tracker */}
                <AnxietyVsReality relationshipId={activeRelationshipId} tier={subscription as any} />

                {/* Attachment Style Journey */}
                <AttachmentJourney relationshipId={activeRelationshipId} tier={subscription as any} />

                {/* Therapist Export */}
                <TherapistExport relationshipId={activeRelationshipId} tier={subscription as any} />
              </>
            )}

            {/* Share Button - Static Embedded */}
            <EnhancedShareButton variant="prominent" />
          </>
        )}
      </div>
    </div>
  );
}